/**
 * FR-018.7 — the one mapping item the editor panel writes (TENANT_CONFIGURATION_UI.md §5).
 *
 * Every mode writes a `confirm` item with `relation: 'is'` (spans belong to the group editor). No
 * item carries `supersedes`: the server supersedes any live row of the same kind / system / label
 * whose range overlaps (F-16) — the WHOLE row, not just the overlapping seasons.
 *
 * - `change`: the row's kind / system / label, the picked target (default: this object), the range.
 *   Because F-16 replaces the whole row, a change that starts after the row does (or ends before
 *   it) first RESTATES the row's other seasons on this object — `[valid_from, min(valid_to, von-1)]`
 *   and `[bis+1, valid_to]` — so they keep their mapping (final review C1). Contiguous items onto
 *   the same target are folded into one, so a change that keeps this object never splits the row.
 * - `accept`: the proposed row restated, target = this object.
 * - `assign`: the stub's label / system, the picked target (required), `valid_from` = the year.
 * - `add`:    this object as target, the picked system + the typed label.
 */
import type { SourceMappingItem, SourceRowDto } from '../../../../services/sourcesService';
import type { SourceEditorMode } from './SourceRow';

/** Lens entity type → registry kind, for modes that have no row kind (`assign` / `add`). */
const LENS_KINDS: Record<string, string> = {
  field: 'field',
  organization: 'party',
  crop: 'product',
};

/** Registry kind → its target (mirrors `libs/tenant_config/hofsync_tenant_config/kinds.py`). */
const KIND_TARGETS: Record<string, { targetKind: 'entity' | 'term'; targetType: string }> = {
  field: { targetKind: 'entity', targetType: 'field' },
  field_span: { targetKind: 'entity', targetType: 'field' },
  location: { targetKind: 'entity', targetType: 'cost_center' },
  segment: { targetKind: 'entity', targetType: 'cost_center' },
  product: { targetKind: 'term', targetType: 'crop' },
  activity: { targetKind: 'term', targetType: 'activity_class' },
  party: { targetKind: 'entity', targetType: 'organization' },
  machine: { targetKind: 'entity', targetType: 'machine' },
  animal: { targetKind: 'entity', targetType: 'animal' },
};

const SEASON_KINDS = new Set(['field', 'field_span', 'machine']);

export function kindForEntityType(entityType: string): string | null {
  return LENS_KINDS[entityType] ?? null;
}

/** The registry kind the editor writes: the row's own kind when it has one, else the lens's. */
export function editorKind(mode: SourceEditorMode, row: SourceRowDto | undefined, entityType: string): string | null {
  if ((mode === 'change' || mode === 'accept' || mode === 'assign') && row?.registry_kind) return row.registry_kind;
  return kindForEntityType(entityType);
}

/** Year selects show only for season-scoped kinds; the server's `validity_kind` wins for a mapping row. */
export function isSeasonalEditor(mode: SourceEditorMode, row: SourceRowDto | undefined, entityType: string): boolean {
  if ((mode === 'change' || mode === 'accept') && row) return row.validity_kind === 'season';
  const kind = editorKind(mode, row, entityType);
  return kind !== null && SEASON_KINDS.has(kind);
}

export interface EditorItemInput {
  mode: SourceEditorMode;
  row?: SourceRowDto;
  stub?: { source_system: string | null; source_label: string } | null;
  entityType: string;
  entityId: string;
  /** The picked target (search hit); `null` keeps this object for `change`. */
  targetId: string | null;
  /** `add` only. */
  system: string;
  /** `add` only. */
  label: string;
  from: number | null;
  to: number | null;
  /** The object the intent was opened on; a mismatch with `entityType`/`entityId` refuses to build. */
  intentEntity?: { entityType: string; entityId: string };
}

export type EditorItemResult =
  | {
      status: 'ready';
      /** The item carrying the picked target (and its range). */
      item: SourceMappingItem;
      /** Every item of the batch, in order: restated seasons around `item`. */
      items: SourceMappingItem[];
      /** The live rows this batch knowingly restates or replaces (the supersede guard's allow-list). */
      accountedIds: string[];
    }
  | { status: 'incomplete' }
  | { status: 'unsupported' }
  /** The intent (and its row) belongs to another object than the panel's — never build from it. */
  | { status: 'stale' };

export function buildEditorItem(input: EditorItemInput): EditorItemResult {
  const { mode, row, stub, entityType, entityId, intentEntity } = input;
  if (intentEntity && (intentEntity.entityType !== entityType || intentEntity.entityId !== entityId)) {
    return { status: 'stale' };
  }
  const kind = editorKind(mode, row, entityType);
  const target = kind ? KIND_TARGETS[kind] : undefined;
  if (!kind || !target) return { status: 'unsupported' };

  let system: string | null;
  let label: string;
  let targetRef: string | null;
  switch (mode) {
    case 'change':
      system = row?.source_system ?? null;
      label = row?.source_label ?? '';
      targetRef = input.targetId ?? entityId;
      break;
    case 'accept':
      system = row?.source_system ?? null;
      label = row?.source_label ?? '';
      targetRef = entityId;
      break;
    case 'assign':
      system = row?.source_system ?? stub?.source_system ?? null;
      label = row?.source_label ?? stub?.source_label ?? '';
      targetRef = input.targetId;
      break;
    case 'add':
      system = input.system || null;
      label = input.label.trim();
      targetRef = entityId;
      break;
  }
  if (!system || !label.trim() || !targetRef) return { status: 'incomplete' };

  const item: SourceMappingItem = {
    action: 'confirm',
    kind,
    source_system: system,
    source_label: label,
    target_ref: targetRef,
    target_kind: target.targetKind,
    target_type: target.targetType,
    relation: 'is',
  };
  // Only a row on this object that the editor restates or replaces is "accounted": `assign` from a stub
  // row and `add` have none, so any row the server would supersede is a surprise.
  const accountedIds = (mode === 'change' || mode === 'accept') && row?.mapping_id ? [row.mapping_id] : [];
  if (!isSeasonalEditor(mode, row, entityType)) return { status: 'ready', item, items: [item], accountedIds };
  if (input.from === null) return { status: 'incomplete' };
  item.valid_from = input.from;
  item.valid_to = input.to;

  const items: SourceMappingItem[] = [item];
  if (mode === 'change' && row && row.valid_from !== null) {
    const restate = (from: number, to: number | null): SourceMappingItem => ({
      ...item,
      relation: row.relation === 'spans' ? 'spans' : 'is',
      target_ref: entityId,
      valid_from: from,
      valid_to: to,
    });
    const from = input.from;
    if (from > row.valid_from) {
      const end = row.valid_to === null ? from - 1 : Math.min(row.valid_to, from - 1);
      items.unshift(restate(row.valid_from, end));
    }
    const to = input.to;
    if (to !== null && to >= row.valid_from && (row.valid_to === null || to < row.valid_to)) {
      items.push(restate(to + 1, row.valid_to));
    }
  }
  return { status: 'ready', item, items: foldContiguous(items), accountedIds };
}

/** Adjacent items onto the same target whose seasons touch become one (`[a, b]` + `[b+1, c]` → `[a, c]`). */
function foldContiguous(items: SourceMappingItem[]): SourceMappingItem[] {
  const out: SourceMappingItem[] = [];
  for (const it of items) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.target_ref === it.target_ref &&
      prev.relation === it.relation &&
      typeof prev.valid_to === 'number' &&
      it.valid_from === prev.valid_to + 1
    ) {
      out[out.length - 1] = { ...prev, valid_to: it.valid_to ?? null };
    } else {
      out.push(it);
    }
  }
  return out;
}
